import { useState } from "react";
import { api } from "@/lib/api";
import { Loader2, X, AlertTriangle, CheckCircle2, XCircle, Zap } from "lucide-react";
import { toast } from "sonner";

/**
 * PlaceStrategyModal — confirmation + execution UI for "Place via DHAN".
 *
 * Flow:
 *   1. Modal opens preview-mode showing all legs + total cost + max P/L
 *   2. User clicks "Place X legs now" → confirms intent
 *   3. POST /dhan/place-strategy { confirm: true }
 *   4. Modal switches to result-mode showing per-leg outcome
 *
 * Atomicity: backend places sequentially with rollback. If a multi-leg
 * strategy partially fills, we show EXACTLY which legs went through and
 * which were rolled back (or are stuck if rollback raced a fill).
 */
function fmtINR(n) {
  if (n === "unlimited" || n === "Unlimited") return "Unlimited";
  if (n === null || n === undefined) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function PlaceStrategyModal({ open, onClose, strategy, ctx, symbol, expiry }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setResult(null);
    setSubmitting(false);
    onClose?.();
  };

  const place = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/dhan/place-strategy", {
        symbol,
        strategy,
        expiry: expiry || "",
        confirm: true,
      });
      setResult(data);
      if (data.ok) {
        toast.success(`${data.placed_orders?.length || 0} leg(s) placed via DHAN`);
      } else {
        toast.error(data.message || "Order placement failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach DHAN");
      setResult({ ok: false, message: e?.response?.data?.detail || "Network error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !strategy) return null;

  const totalUnits = strategy.legs.reduce((acc, l) => acc + l.qty_lots * l.qty_units, 0);
  const isResult = result !== null;

  return (
    <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="place-strategy-modal">
      <div className="bg-[#0a0a0a] border border-zinc-800 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#22C55E] font-mono">Live · DHAN execution</div>
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-white">
              {isResult ? "Order Result" : `Confirm: ${strategy.name}`}
            </h2>
          </div>
          <button onClick={reset} className="text-zinc-500 hover:text-white" data-testid="place-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* PREVIEW MODE */}
          {!isResult && (
            <>
              <div className="text-xs text-zinc-300 leading-relaxed">
                You are about to place <b>{strategy.legs.length} live order{strategy.legs.length > 1 ? "s" : ""}</b> on your DHAN account for <b className="text-[#007AFF]">{symbol}</b> ({totalUnits} total units across all legs).
              </div>

              {/* Legs preview */}
              <div className="space-y-1.5">
                {strategy.legs.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs font-mono py-2 px-3 bg-black/40 border border-zinc-900">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500">#{i + 1}</span>
                      <span className={`font-bold uppercase tracking-widest ${l.action === "BUY" ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{l.action}</span>
                      <span className="text-white">{l.strike} {l.type}</span>
                      <span className="text-zinc-500">×{l.qty_lots} lot ({l.qty_units * l.qty_lots} units)</span>
                    </div>
                    <span className="text-zinc-300">≈ ₹{l.ltp.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* P/L summary */}
              <div className="grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
                <div className="bg-[#0a0a0a] p-2 text-center">
                  <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Max profit</div>
                  <div className="text-xs font-bold text-[#22C55E]">{fmtINR(strategy.max_profit)}</div>
                </div>
                <div className="bg-[#0a0a0a] p-2 text-center">
                  <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Max loss</div>
                  <div className="text-xs font-bold text-[#EF4444]">{fmtINR(strategy.max_loss)}</div>
                </div>
                <div className="bg-[#0a0a0a] p-2 text-center">
                  <div className="text-[9px] uppercase tracking-widest font-mono text-zinc-500">Margin</div>
                  <div className="text-xs font-bold text-zinc-200">{fmtINR(strategy.margin_required)}</div>
                </div>
              </div>

              {/* Warning */}
              <div className="border-2 border-[#F59E0B] bg-[#F59E0B]/10 p-3 flex gap-2 items-start">
                <AlertTriangle className="h-4 w-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-zinc-300 leading-relaxed">
                  <b className="text-[#F59E0B]">Real money</b>. Orders go to DHAN as <b>MARKET / INTRADAY (MIS)</b>. If any leg fails mid-batch we attempt automatic rollback, but a fill can race the cancel. Pre-flight your margin in DHAN if unsure.
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={reset}
                  disabled={submitting}
                  className="flex-1 text-xs uppercase tracking-widest border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white py-2.5 font-mono disabled:opacity-50"
                  data-testid="place-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={place}
                  disabled={submitting}
                  className="flex-[2] text-xs uppercase tracking-widest border border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] disabled:opacity-50 py-2.5 font-mono font-bold flex items-center justify-center gap-2"
                  data-testid="place-modal-submit"
                >
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  {submitting ? "Placing…" : `Place ${strategy.legs.length} leg${strategy.legs.length > 1 ? "s" : ""} now`}
                </button>
              </div>
            </>
          )}

          {/* RESULT MODE */}
          {isResult && (
            <>
              <div className={`p-3 border-2 ${result.ok ? "border-[#22C55E] bg-[#22C55E]/10" : "border-[#EF4444] bg-[#EF4444]/10"} flex gap-2 items-start`}>
                {result.ok
                  ? <CheckCircle2 className="h-5 w-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                  : <XCircle className="h-5 w-5 text-[#EF4444] flex-shrink-0 mt-0.5" />}
                <div className="text-sm text-zinc-200 leading-relaxed">{result.message}</div>
              </div>

              {/* Placed orders */}
              {result.placed_orders?.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">Placed orders</div>
                  {result.placed_orders.map((o) => (
                    <div key={o.order_id} className="flex items-center justify-between text-xs font-mono py-2 px-3 bg-black/40 border border-zinc-900">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="h-3 w-3 text-[#22C55E] flex-shrink-0" />
                        <span className={`font-bold ${o.action === "BUY" ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{o.action}</span>
                        <span className="text-white truncate">{o.trading_symbol || `${o.strike} ${o.type}`}</span>
                        <span className="text-zinc-500">×{o.qty_units}</span>
                      </div>
                      <span className="text-[#007AFF] text-[10px]">#{o.order_id}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Rollback note */}
              {result.rollback_results?.length > 0 && (
                <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-3 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-widest font-mono text-[#F59E0B]">Rollback attempted</div>
                  {result.rollback_results.map((rr) => (
                    <div key={rr.order_id} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-zinc-400">#{rr.order_id}</span>
                      <span className={rr.ok ? "text-[#22C55E]" : "text-[#EF4444]"}>{rr.ok ? "cancelled" : "couldn't cancel — verify in DHAN"}</span>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={reset}
                className="w-full text-xs uppercase tracking-widest border border-zinc-800 hover:border-zinc-600 text-zinc-200 py-2.5 font-mono"
                data-testid="place-modal-done"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
